class RefillPodShipmentBreakdown extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.eligibleVariantId = String(
      this.dataset.eligibleVariantId || ''
    );

    this.updateTimer = null;
    this.abortController = new AbortController();

    this.handleVariantChange =
      this.handleVariantChange.bind(this);

    this.handleDocumentChange =
      this.handleDocumentChange.bind(this);

    this.scheduleUpdate =
      this.scheduleUpdate.bind(this);

    this.initialize();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.formObserver?.disconnect();
    window.clearTimeout(this.updateTimer);
  }

  initialize() {
    this.form = this.getProductForm();

    /*
     * The block can be inserted before the product form during
     * Shopify section re-rendering, so retry on the next frame.
     */
    if (!this.form) {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        this.form = this.getProductForm();

        if (!this.form) {
          console.warn(
            'Refill shipment breakdown: product form not found.',
            this.formId
          );

          return;
        }

        this.setupListeners();
      });

      return;
    }

    this.setupListeners();
  }

  getProductForm() {
    return (
      document.getElementById(this.formId) ||
      document.forms[this.formId] ||
      null
    );
  }

  setupListeners() {
    const signal = this.abortController.signal;

    /*
     * Existing theme event dispatched whenever the selected
     * Shopify product variant changes.
     */
    this.form.addEventListener(
      'variant:change',
      this.handleVariantChange,
      { signal }
    );

    /*
     * Ordergroove purchase options may be outside the Shopify form,
     * so listen for changes through document-level delegation.
     */
    document.addEventListener(
      'change',
      this.handleDocumentChange,
      { signal }
    );

    /*
     * Ordergroove adds the selling_plan input for Subscribe & Save
     * and removes it for One Time Purchase. Observe the product form
     * so the display updates when that happens.
     */
    this.formObserver = new MutationObserver(
      this.scheduleUpdate
    );

    this.formObserver.observe(this.form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value']
    });

    this.update();
  }

  handleVariantChange() {
    this.scheduleUpdate();
  }

  handleDocumentChange(event) {
    const productSection = this.closest('.shopify-section');

    /*
     * Ignore changes from other parts of the page where possible.
     */
    if (
      productSection &&
      !productSection.contains(event.target)
    ) {
      return;
    }

    this.scheduleUpdate();
  }

  scheduleUpdate() {
    window.clearTimeout(this.updateTimer);

    /*
     * Allow Ordergroove time to add/remove/update
     * the hidden selling_plan input.
     */
    this.updateTimer = window.setTimeout(() => {
      this.update();
    }, 100);
  }

  getSelectedVariantId() {
    const variantInput =
      this.form.elements.namedItem('id');

    return String(variantInput?.value || '');
  }

  isSubscriptionSelected() {
    const sellingPlanInput =
      this.form.elements.namedItem('selling_plan');

    return (
      String(sellingPlanInput?.value || '').trim() !== ''
    );
  }

  update() {
    if (!this.form) return;

    const selectedVariantId =
      this.getSelectedVariantId();

    const isEligibleVariant =
      selectedVariantId === this.eligibleVariantId;

    const shouldShow =
      isEligibleVariant &&
      this.isSubscriptionSelected();

    this.toggleAttribute('hidden', !shouldShow);
    this.setAttribute(
      'aria-hidden',
      shouldShow ? 'false' : 'true'
    );
  }
}

if (
  !customElements.get(
    'refill-pod-shipment-breakdown'
  )
) {
  customElements.define(
    'refill-pod-shipment-breakdown',
    RefillPodShipmentBreakdown
  );
}